import { useQuery } from '@tanstack/react-query';

import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import { PaymentPricing } from '@/types/payment-account';

export const paymentPricingKeys = {
  all: ['payment-pricing'] as const,
};

/**
 * `GET /payment-account/pricing` (T1.6, contrato assumido — o endpoint ainda
 * não existe). Única fonte de taxa, prazo, limite e "pode ativar" mostrados
 * no app: nenhuma tela escreve esses números direto, todas recebem este
 * objeto por parâmetro (`app/content/payments/`, `utils/payment-simulator.ts`).
 *
 * T1.6 marca o endpoint como autenticado, por isso espera a store de auth
 * hidratar. A versão sem login (central de ajuda pública, T8.1) é
 * `usePublicPaymentPricing`, em `use-public-payment-pricing.ts` — arquivo à
 * parte em vez de tornar este hook condicional.
 */
export function usePaymentPricing(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentPricingKeys.all,
    queryFn: async () => (await api.get<PaymentPricing>('/payment-account/pricing')).data,
    enabled: isHydrated && options?.enabled !== false,
    // Muda por decisão de negócio (platform_settings), não por evento do
    // usuário: cache de alguns minutos evita bater no endpoint a cada tela.
    staleTime: 5 * 60 * 1000,
    // 403 (T7.1: o endpoint passou a exigir `canManage(ctx)`) não muda
    // tentando de novo — profissional em caixa único sempre vai receber o
    // mesmo 403. Sem isto, o `retry: 1` padrão (`lib/query-client.ts`) gasta
    // uma tentativa extra antes de assentar em erro.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 403) return false;
      return failureCount < 1;
    },
  });
}
